// src/pages/PaymentAccounting/index.tsx
// Màn Kế toán Chi trả (Lệnh chuyển tiền qua Bank API) — dùng LUÔN bảng PrizeClaimDossier: hồ sơ đã Thẩm định
// (Status = Verified) thì hiển thị ở đây để Kế toán "Chuyển khoản" (ChuaChi -> DaChi). Chưa tích hợp Bank API
// thật (xem PayPrizeClaimDossierCommand) — thao tác "Chuyển khoản" hiện chỉ ghi nhận trạng thái đã chi.
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, CardHeader, CardBody, Button, Input, Label, Modal, ModalBody } from 'reactstrap';
import BreadCrumb from 'Components/Common/BreadCrumb';
import PaymentAccountingTable from './PaymentAccountingTable';
import { fetchClaimDossiers, payClaimDossier } from 'slices/claimDossier/thunk';
import { PrizeClaimDossier, ClaimDossierPaymentStatus, CLAIM_DOSSIER_PAYMENT_STATUS_LABELS } from 'pages/ClaimDossier/types';
import { useBrandingTitle } from '../../hooks';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('payment_accounting');

const PAGE_SIZE = 20;

const PaymentAccountingPage: React.FC = () => {
  useBrandingTitle(st('ke_toan_chi_tra_text'));

  const location = useLocation();
  const dispatch = useDispatch<any>();
  const { dossiers, loading, payingId } = useSelector((state: any) => state.ClaimDossier);

  const [paymentStatusFilter, setPaymentStatusFilter] = useState<ClaimDossierPaymentStatus | ''>('');
  const [pendingPay, setPendingPay] = useState<PrizeClaimDossier | null>(null);

  const fetchList = () =>
    dispatch(
      fetchClaimDossiers({
        PageIndex: 1,
        PageSize: PAGE_SIZE,
        // Chỉ hồ sơ ĐÃ Thẩm định mới đủ điều kiện chi trả — khớp PayPrizeClaimDossierCommand.
        Status: 'Verified',
        PaymentStatus: paymentStatusFilter || undefined,
        // Bảng chỉ hiển thị cột của chính hồ sơ + tên người chi => không cần chấm lại vé/mã kỳ/đại lý/thẩm định.
        Lite: true,
      })
    );

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStatusFilter]);

  const handleConfirmPay = () => {
    if (!pendingPay) return;
    dispatch(payClaimDossier(pendingPay.id)).then((action: any) => {
      if (payClaimDossier.fulfilled.match(action)) fetchList();
    });
    setPendingPay(null);
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={st('ke_toan_chi_tra_text')} pageTitle={st('tra_thuong_text')} />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="card-header border-0">
                  <Row className="align-items-center gy-3">
                    <div className="col-sm">
                      <h5 className="card-title mb-0">{st('ke_toan_chi_tra_title')}</h5>
                    </div>
                    <div className="col-sm-auto">
                      <div className="d-flex gap-1 flex-wrap">
                        <Button color="soft-secondary" onClick={fetchList}>
                          <i className="ri-refresh-line"></i>
                        </Button>
                      </div>
                    </div>
                  </Row>
                </CardHeader>
                <CardBody className="pt-0">
                  <Row className="g-3 mb-5 align-items-end">
                    <Col md={3}>
                      <Label className="form-label">{st('trang_thai_chi_label')}</Label>
                      <Input
                        type="select"
                        className="form-select"
                        value={paymentStatusFilter}
                        onChange={(e) => setPaymentStatusFilter(e.target.value as ClaimDossierPaymentStatus | '')}
                      >
                        <option value="">{st('tat_ca_trang_thai_chi_option')}</option>
                        {Object.keys(CLAIM_DOSSIER_PAYMENT_STATUS_LABELS).map((value) => (
                          <option key={value} value={value}>
                            {(CLAIM_DOSSIER_PAYMENT_STATUS_LABELS as any)[value]}
                          </option>
                        ))}
                      </Input>
                    </Col>
                  </Row>
                  <PaymentAccountingTable
                    dossiers={dossiers}
                    loading={loading}
                    payingId={payingId}
                    onPay={setPendingPay}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={!!pendingPay} toggle={() => setPendingPay(null)} centered>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <i className="ri-bank-card-line display-5 text-primary"></i>
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{st('xac_nhan_chuyen_khoan_title')}</h4>
              <p className="text-muted mx-4 mb-0">
                {st('chuyen_text')} <b>{Number(pendingPay?.netAmount || 0).toLocaleString('vi-VN')} {st('d_text')}</b> {st('toi_tai_khoan_text')}{' '}
                <b>{pendingPay?.bankAccountNumber}</b> ({pendingPay?.bankAccountName} — {pendingPay?.bankName}{st('cho_ho_so_text')} <b>{pendingPay?.dossierCode}</b>?
              </p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <Button color="light" className="w-sm" onClick={() => setPendingPay(null)}>
              {st('dong_button')}
            </Button>
            {PageActionPermission.canDisplay(location.pathname, 'button-pay-confirm') && (
              <Button
                color="primary"
                className="w-sm"
                data-action-code="button-pay-confirm"
                data-action-name="Xác nhận chuyển khoản"
                onClick={handleConfirmPay}
              >
                {st('co_chuyen_khoan_button')}
              </Button>
            )}
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default PaymentAccountingPage;
